import { useFormSchema } from "@portal/providers/FormSchemaProvider";
import type { ReactNode } from "react";
import { isFieldRequiredForSubmit } from "../../utils/schema-fields";
import { cn } from "../lib/utils";
import { PartialIndicator, Text } from "../primitives";

type FormSectionProps = {
	header: ReactNode;
	headerClassName?: string;
	partial?: boolean;
	/** When set with a FormSchemaProvider schema, derives hard-required asterisk unless `required` is passed. */
	name?: string;
	/**
	 * Explicit hard-required asterisk. Wins over schema derivation from `name`.
	 *
	 * @deprecated Prefer `name` + `FormSchemaProvider` so the asterisk is schema-derived.
	 * Explicit `required` will be replaced under
	 * [Form Standardization](https://linear.app/inrev/project/form-standardization-a77aa9131bef).
	 */
	required?: boolean;
	subHeader?: ReactNode;
	subHeaderClassName?: string;
	contentClassName?: string;
	indent?: true;
	id?: string;
	include?: boolean;
	topPadding?: true;
	children?: ReactNode;
};

/**
 * Groups form fields under a titled section with an optional sub-header and partial indicator.
 */
export const FormSection = ({
	header,
	headerClassName,
	partial,
	name,
	required: requiredProp,
	subHeader,
	subHeaderClassName,
	contentClassName,
	indent,
	children,
	id,
	include = true,
	topPadding,
}: FormSectionProps) => {
	const schema = useFormSchema();
	const required =
		requiredProp ?? (name && schema ? isFieldRequiredForSubmit(schema, name) : false);

	if (!include) return null;

	return (
		<div
			id={id}
			className={cn("w-full flex flex-col h-fit min-h-fit", topPadding ? "mt-[15px]" : undefined)}
			data-testid="form-section"
		>
			<div className="w-full flex flex-col space-y-[8px]">
				<div
					className={cn("relative flex items-center space-x-[10px] h-fit w-full", headerClassName)}
				>
					<Text type="title" color="primary">
						{header}
						{required && <Text color="danger">{"\u00A0"}*</Text>}
					</Text>
					{partial && <PartialIndicator active={true} className="mt-[1px]" />}
				</div>
				{subHeader && (
					<Text
						type="body"
						color="secondary"
						className={cn("italic !mb-[5px]", subHeaderClassName)}
					>
						{subHeader}
					</Text>
				)}
			</div>
			<div
				className={cn(
					"w-full flex flex-col mt-[20px]",
					indent ? "px-[5px]" : undefined,
					contentClassName,
				)}
			>
				{children}
			</div>
		</div>
	);
};
